import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Logo } from "@/components/ui/Logo";
import { RingBadge } from "@/components/ui/RingBadge";
import { plainText } from "@/lib/plainText";
import { toolOrigin, type CatalogEntry } from "@/lib/toolSearch";

/** Guild's own mark (the "G" tile), shown for tools Guild built, which have no Trelica logo. */
const GUILD_LOGO = `${import.meta.env.BASE_URL}favicon.svg`;

// The card for a catalog entry in a browse grid — a tool on the Tools tab, a radar practice on
// the Standards tab.

/** One card in the grid: logo (tools) or a "practice" label, name, kind or category, ring and
 * status, a few lines of description, what it's confirmed as meeting, and every flag it has —
 * "not yet reviewed" as a quiet note, the rest as warnings. Marking reviewed happens only on
 * the detail page. */
export function CatalogCard({ entry: e, standardNames }: { entry: CatalogEntry; standardNames: Map<string, string> }) {
  const x = e.item;
  const linked = e.type === "tool" ? e.item.satisfies_standards : e.item.related_standards;
  const warnings = x.flags.filter((f) => f.kind !== "unreviewed");
  const unreviewed = x.flags.some((f) => f.kind === "unreviewed");
  return (
    <Card className="flex h-full cursor-pointer flex-col hover:border-accent-line">
      <div className="mb-1 flex items-center gap-3">
        {e.type === "tool" && (
          <Logo
            src={toolOrigin(e.item) === "Guild-built" ? GUILD_LOGO : e.item.logo_url}
            name={x.name}
            className="h-9 w-9"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="truncate font-medium">{x.name}</h3>
            <div className="flex shrink-0 gap-1">
              {x.ring && <RingBadge ring={x.ring} />}
              {x.status === "deprecated" && <Badge tone="warn">deprecated</Badge>}
            </div>
          </div>
          <p className="truncate text-xs text-text/50">
            {e.type === "tool" ? [e.item.category, toolOrigin(e.item)].filter(Boolean).join(" · ") : "Practice"}
          </p>
        </div>
      </div>
      <p className="mt-2 line-clamp-4 text-sm text-text/70">{plainText(x.what)}</p>
      <p className="mt-2 text-xs text-text/50">
        {e.type === "practice" && e.item.same_as_standard
          ? `The radar's view of ${standardNames.get(e.item.same_as_standard) ?? e.item.same_as_standard}`
          : linked.length > 0
            ? `${e.type === "tool" ? "Satisfies" : "A way to meet"} ${linked.map((sid) => standardNames.get(sid) ?? sid).join(", ")}`
            : "Linked to no standard yet"}
      </p>
      {(warnings.length > 0 || unreviewed) && (
        <ul className="mt-2 flex flex-col gap-0.5 border-t border-line pt-2 text-xs">
          {warnings.map((f, i) => (
            <li key={i} className="text-warn">
              {f.message}
            </li>
          ))}
          {unreviewed && <li className="text-text/50">Not yet reviewed</li>}
        </ul>
      )}
    </Card>
  );
}
